import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  Package,
  Plus,
  RefreshCw,
  Search,
  Send,
  ShoppingBag,
  Wallet,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { formatPrice, formatDate, formatDateTime, PAYMENT_LABELS, ORDER_STATUS } from "@/lib/format";
import RichText from "@/components/RichText";
import OrderReceiptButton from "@/components/OrderReceiptButton";
import PhoneField, { isValidPhoneNumber } from "@/components/PhoneField";

function useFetch(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api
      .get(url)
      .then(({ data: payload }) => setData(payload))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, [url]);
  return { data, loading, reload: load };
}

function Panel({ title, description, children, action }) {
  return (
    <section className="rounded-2xl border border-line bg-white p-5 sm:p-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-ink">{title}</h2>
          {description && <p className="mt-1 text-xs text-slate-500">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Empty({ label, cta }) {
  return (
    <div className="rounded-xl border border-dashed border-line py-12 text-center">
      <p className="text-sm text-slate-500">{label}</p>
      {cta}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-12">
      <Loader2 className="h-6 w-6 animate-spin text-brand" />
    </div>
  );
}

export function AccountDashboard() {
  const { data, loading } = useFetch("/my/dashboard");
  const { currency } = useStore();

  const stats = [
    { label: "Commandes", value: data?.orders_count ?? 0, icon: ShoppingBag },
    { label: "Licences", value: data?.licenses_count ?? 0, icon: KeyRound },
    { label: "Total dépensé", value: formatPrice(data?.total_spent ?? 0, currency), icon: Wallet },
    { label: "Tickets ouverts", value: data?.open_tickets ?? 0, icon: Package },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="account-stats">
        {stats.map(({ label, value, icon: Icon }) => (
          <div key={label} className="rounded-2xl border border-line bg-white p-4">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-light text-brand">
              <Icon className="h-4 w-4" />
            </span>
            <p className="mt-3 font-display text-xl font-extrabold text-ink">{value}</p>
            <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
          </div>
        ))}
      </div>

      <Panel
        title="Dernières commandes"
        description="Vos achats les plus récents"
        action={
          <Link to="/compte/commandes" className="text-xs font-bold text-brand hover:underline">
            Tout voir
          </Link>
        }
      >
        {loading ? (
          <Spinner />
        ) : data?.recent_orders?.length ? (
          <div className="space-y-3">
            {data.recent_orders.map((order) => (
              <div
                key={order.id}
                data-testid={`dashboard-order-${order.order_number}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line p-4"
              >
                <div>
                  <p className="text-sm font-bold text-ink">{order.order_number}</p>
                  <p className="text-xs text-slate-500">{formatDateTime(order.created_at)}</p>
                </div>
                <span
                  className={`rounded-full border px-3 py-1 text-[11px] font-bold ${
                    ORDER_STATUS[order.status]?.className || "border-line text-slate-500"
                  }`}
                >
                  {ORDER_STATUS[order.status]?.label || order.status}
                </span>
                <p className="font-display text-base font-extrabold text-ink">
                  {formatPrice(order.total, currency)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <Empty
            label="Aucune commande pour le moment."
            cta={
              <Link to="/boutique" className="mt-4 inline-block rounded-full bg-brand px-5 py-2.5 text-xs font-bold text-white">
                Découvrir la boutique
              </Link>
            }
          />
        )}
      </Panel>
    </div>
  );
}

export function AccountOrders() {
  const { data, loading } = useFetch("/my/orders");
  const { currency } = useStore();
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({});
  const [deliveries, setDeliveries] = useState({});
  const [revealedKeys, setRevealedKeys] = useState({});
  const [shownTexts, setShownTexts] = useState({});

  const fmtSize = (b) =>
    !b ? "" : b >= 1e9 ? `${(b / 1e9).toFixed(2)} Go` : b >= 1e6 ? `${(b / 1e6).toFixed(2)} Mo` : `${Math.max(1, Math.round(b / 1e3))} Ko`;

  const toggleDownloads = async (order) => {
    const id = order.id;
    if (expanded[id]) {
      setExpanded((e) => ({ ...e, [id]: false }));
      return;
    }
    setExpanded((e) => ({ ...e, [id]: true }));
    if (!deliveries[id]) {
      try {
        const { data: payload } = await api.get(`/my/delivery/${id}`);
        setDeliveries((d) => ({ ...d, [id]: payload }));
      } catch {
        /* accès refusé ou erreur réseau */
      }
    }
  };

  const copyKey = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Clé copiée !");
    } catch {
      toast.error("Copie impossible — sélectionnez la clé manuellement");
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = (data || []).filter((order) => {
    if (!q) return true;
    return (
      order.order_number.toLowerCase().includes(q) ||
      (order.tracking_number || "").toLowerCase().includes(q) ||
      (ORDER_STATUS[order.status]?.label || order.status || "").toLowerCase().includes(q) ||
      (order.items || []).some((item) => item.name.toLowerCase().includes(q))
    );
  });

  return (
    <Panel
      title="Mes commandes"
      description="Historique complet de vos achats"
      action={
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="orders-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="N° commande, produit, suivi, statut…"
            className="h-10 w-full rounded-full border border-line pl-10 pr-4 text-xs focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10"
          />
        </div>
      }
    >
      {q && !loading && (
        <p className="mb-3 text-xs text-slate-500" data-testid="orders-search-count">
          {filtered.length} commande{filtered.length > 1 ? "s" : ""} trouvée{filtered.length > 1 ? "s" : ""} pour « {query} »
        </p>
      )}
      {loading ? (
        <Spinner />
      ) : filtered.length ? (
        <div className="space-y-3" data-testid="orders-list">
          {filtered.map((order) => (
            <div key={order.id} className="rounded-xl border border-line p-4" data-testid={`order-row-${order.order_number}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-ink">{order.order_number}</p>
                  <p className="text-xs text-slate-500">
                    {formatDateTime(order.created_at)} · {PAYMENT_LABELS[order.payment_method] || order.payment_method}
                    {order.tracking_number ? ` · Suivi : ${order.tracking_number}` : ""}
                  </p>
                </div>
                <span
                  className={`rounded-full border px-3 py-1 text-[11px] font-bold ${
                    ORDER_STATUS[order.status]?.className || "border-line text-slate-500"
                  }`}
                >
                  {ORDER_STATUS[order.status]?.label || order.status}
                </span>
                {order.digital_delivery && (
                  <span
                    className={`flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-bold ${
                      order.digital_delivery.status === "released"
                        ? "border-brand/20 bg-brand-light text-brand"
                        : "border-amber-200 bg-amber-50 text-amber-700"
                    }`}
                    data-testid={`order-digital-badge-${order.order_number}`}
                  >
                    <Package className="h-3 w-3" /> Livraison : Numérique
                  </span>
                )}
                <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                  <p className="font-display text-base font-extrabold text-ink">
                    {formatPrice(order.total, currency)}
                  </p>
                  {order.payment_status !== "paid" && ["pending", "payment_rejected"].includes(order.status) && (
                    <Link
                      to={`/paiement/${order.order_number}`}
                      data-testid={`order-pay-${order.order_number}`}
                      className="flex items-center gap-1.5 rounded-full bg-brand px-3 py-2 text-xs font-bold text-white hover:bg-brand/90"
                    >
                      💳 Payer
                    </Link>
                  )}
                  {order.digital_delivery && (
                    <button
                      type="button"
                      onClick={() => toggleDownloads(order)}
                      data-testid={`order-downloads-toggle-${order.order_number}`}
                      className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold transition-colors duration-200 ${
                        order.digital_delivery.status === "released"
                          ? "bg-brand text-white hover:bg-brand/90"
                          : "border border-amber-200 bg-amber-50 text-amber-700"
                      }`}
                    >
                      <Download className="h-3 w-3" />
                      {expanded[order.id] ? "Masquer" : "Voir les téléchargements"}
                    </button>
                  )}
                  <Link
                    to={`/compte/commandes/${order.id}`}
                    data-testid={`order-detail-${order.order_number}`}
                    className="flex items-center gap-1.5 rounded-full border border-line px-3 py-2 text-xs font-bold text-ink hover:border-brand hover:text-brand"
                  >
                    Détails <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 border-t border-line pt-3">
                {order.items.map((item) => (
                  <span key={item.product_id} className="flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5">
                    <img src={item.image_url} alt="" className="h-7 w-7 object-contain" />
                    <span className="text-[11px] font-semibold text-ink">
                      {item.name} × {item.quantity}
                    </span>
                  </span>
                ))}
              </div>
              {(order.licenses || [])
                .filter((lic) => !lic.key_value || (lic.fields || []).length > 0)
                .map((lic, i) => (
                  <div
                    key={i}
                    className="mt-3 rounded-lg border border-line bg-surface p-3"
                    data-testid={`order-activation-${order.order_number}-${i}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs font-bold text-ink">{lic.product_name}</p>
                      <span
                        className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold ${
                          lic.status === "delivered"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-amber-200 bg-amber-50 text-amber-700"
                        }`}
                      >
                        {lic.status === "delivered" ? "Activé ✓" : "En cours de traitement"}
                      </span>
                    </div>
                    {(lic.fields || []).length > 0 && (
                      <dl className="mt-2 space-y-0.5">
                        {lic.fields.map((field) => (
                          <div key={field.key} className="flex gap-1.5 text-[11px]">
                            <dt className="text-slate-500">{field.label} :</dt>
                            <dd className="font-semibold text-ink">{field.value}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                    {lic.status === "delivered" && lic.admin_message ? (
                      <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2.5">
                        <p className="whitespace-pre-line text-[11px] leading-relaxed text-emerald-800">
                          <RichText text={lic.admin_message} />
                        </p>
                        {lic.credentials && (
                          <code className="mt-1.5 block rounded bg-white px-2 py-1.5 font-mono text-[11px] font-bold text-emerald-700">
                            {lic.credentials}
                          </code>
                        )}
                      </div>
                    ) : !lic.key_value && lic.status !== "delivered" ? (
                      <p className="mt-2 text-[11px] text-amber-700">
                        Activation envoyée sous {lic.delivery_time || "1 à 60 minutes"} — le résultat s'affichera ici.
                      </p>
                    ) : null}
                  </div>
                ))}
              {order.digital_delivery && expanded[order.id] && (
                <div className="mt-3 rounded-xl border border-brand/15 bg-brand-soft/40 p-4" data-testid={`order-downloads-panel-${order.order_number}`}>
                  {!deliveries[order.id] ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin text-brand" />
                    </div>
                  ) : deliveries[order.id].locked ? (
                    <p className="text-xs font-semibold leading-relaxed text-amber-700" data-testid={`order-downloads-locked-${order.order_number}`}>
                      Paiement en attente — le lien de téléchargement se débloque automatiquement dès la confirmation de votre paiement.
                    </p>
                  ) : (
                    <>
                      <p className="mb-2 text-xs font-extrabold text-ink">
                        Vos contenus numériques ({deliveries[order.id].items_count})
                      </p>
                      <div className="space-y-2">
                        {deliveries[order.id].items.map((item) => {
                          const exhausted = item.download_limit > 0 && item.downloads_used >= item.download_limit;
                          const blocked = item.is_expired || exhausted;
                          return (
                            <div
                              key={item.id}
                              className="flex flex-wrap items-center gap-2.5 rounded-lg border border-line bg-white p-2.5"
                              data-testid={`order-content-${order.order_number}-${item.id}`}
                            >
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-light text-brand">
                                {item.type === "key" ? <KeyRound className="h-3.5 w-3.5" />
                                  : item.type === "link" ? <ExternalLink className="h-3.5 w-3.5" />
                                  : item.type === "text" || item.type === "image" ? <Eye className="h-3.5 w-3.5" />
                                  : <Download className="h-3.5 w-3.5" />}
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-xs font-bold text-ink">{item.title}</p>
                                <p className="truncate text-[11px] text-slate-400">
                                  {item.file_name ? `${item.file_name} · ${fmtSize(item.file_size)} · ` : ""}
                                  {item.expires_at ? `Expire le ${item.expires_at} · ` : ""}
                                  {item.download_limit > 0
                                    ? `Téléchargements : ${item.downloads_used} / ${item.download_limit}`
                                    : "Accès illimité"}
                                </p>
                                {item.type === "key" && revealedKeys[item.id] && (
                                  <code className="mt-1 block w-fit rounded bg-slate-50 px-2 py-1 font-mono text-[11px] font-bold text-brand">
                                    {item.value}
                                  </code>
                                )}
                                {item.type === "text" && shownTexts[item.id] && (
                                  <p className="mt-1 whitespace-pre-line rounded bg-slate-50 p-2 text-[11px] leading-relaxed text-slate-600">
                                    {item.content}
                                  </p>
                                )}
                              </div>
                              {item.is_expired ? (
                                <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[10px] font-bold text-rose-700">Expiré</span>
                              ) : exhausted ? (
                                <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-bold text-amber-700">Limite atteinte</span>
                              ) : (
                                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-700">
                                  {item.type === "file" ? "Prêt à télécharger" : "Disponible"}
                                </span>
                              )}
                              {item.type === "file" || item.type === "image" ? (
                                <Link
                                  to={blocked ? "#" : `/compte/commandes/${order.id}/telechargement/${item.id}`}
                                  data-testid={`order-content-download-${item.id}`}
                                  onClick={blocked ? (e) => e.preventDefault() : undefined}
                                  className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold ${
                                    blocked ? "cursor-not-allowed bg-slate-100 text-slate-400" : "bg-brand text-white hover:bg-brand/90"
                                  }`}
                                >
                                  <Download className="h-3 w-3" /> Télécharger
                                </Link>
                              ) : item.type === "link" ? (
                                <button
                                  type="button"
                                  data-testid={`order-content-link-${item.id}`}
                                  onClick={() => window.open(item.url, "_blank", "noopener")}
                                  className="flex items-center gap-1.5 rounded-full border border-brand/30 px-3 py-2 text-xs font-bold text-brand hover:bg-brand-light"
                                >
                                  <ExternalLink className="h-3 w-3" /> Accéder au lien
                                </button>
                              ) : item.type === "key" ? (
                                <span className="flex items-center gap-1.5">
                                  <button
                                    type="button"
                                    data-testid={`order-content-key-${item.id}`}
                                    onClick={() => setRevealedKeys((r) => ({ ...r, [item.id]: !r[item.id] }))}
                                    className="flex items-center gap-1.5 rounded-full border border-brand/30 px-3 py-2 text-xs font-bold text-brand hover:bg-brand-light"
                                  >
                                    {revealedKeys[item.id] ? <EyeOff className="h-3 w-3" /> : <KeyRound className="h-3 w-3" />}
                                    {revealedKeys[item.id] ? "Masquer" : "Afficher la clé"}
                                  </button>
                                  {revealedKeys[item.id] && (
                                    <button
                                      type="button"
                                      data-testid={`order-content-copy-${item.id}`}
                                      onClick={() => copyKey(item.value)}
                                      className="flex items-center gap-1 rounded-full border border-line px-3 py-2 text-xs font-bold text-slate-600 hover:border-brand hover:text-brand"
                                    >
                                      <Copy className="h-3 w-3" /> Copier
                                    </button>
                                  )}
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  data-testid={`order-content-text-${item.id}`}
                                  onClick={() => setShownTexts((s) => ({ ...s, [item.id]: !s[item.id] }))}
                                  className="flex items-center gap-1.5 rounded-full border border-brand/30 px-3 py-2 text-xs font-bold text-brand hover:bg-brand-light"
                                >
                                  <Eye className="h-3 w-3" /> {shownTexts[item.id] ? "Masquer" : "Voir le contenu"}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : data?.length ? (
        <Empty label={`Aucune commande ne correspond à « ${query} ».`} />
      ) : (
        <Empty label="Vous n'avez pas encore passé de commande." />
      )}
    </Panel>
  );
}

export function AccountLicenses() {
  const { data, loading } = useFetch("/my/licenses");
  const [revealed, setRevealed] = useState({});

  const copy = (value) => {
    navigator.clipboard?.writeText(value);
    toast.success("Clé copiée");
  };

  return (
    <Panel title="Mes licences" description="Toutes vos clés et instructions de livraison">
      {loading ? (
        <Spinner />
      ) : data?.length ? (
        <div className="space-y-3" data-testid="licenses-list">
          {data.map((license, i) => (
            <div key={`${license.order_number}-${i}`} className="rounded-xl border border-line p-4" data-testid={`license-row-${i}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-bold text-ink">{license.product_name}</p>
                <span className="flex items-center gap-2">
                  {!license.key_value && (
                    <span
                      className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold ${
                        license.status === "delivered"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-amber-200 bg-amber-50 text-amber-700"
                      }`}
                    >
                      {license.status === "delivered" ? "Activé ✓" : "En traitement"}
                    </span>
                  )}
                  <span className="text-xs text-slate-500">
                    {license.order_number} · {formatDate(license.purchased_at)}
                  </span>
                </span>
              </div>

              {license.key_value ? (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <code className="flex-1 rounded-lg border border-brand/20 bg-brand-soft px-3 py-2.5 font-mono text-[13px] font-bold tracking-wider text-brand">
                    {revealed[i] ? license.key_value : `XXXXX-XXXXX-XXXXX-${license.key_value.slice(-5)}`}
                  </code>
                  <button
                    data-testid={`reveal-license-${i}`}
                    onClick={() => setRevealed((r) => ({ ...r, [i]: !r[i] }))}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-line text-slate-500 hover:text-brand"
                  >
                    {revealed[i] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button
                    data-testid={`copy-license-btn-${i}`}
                    onClick={() => copy(license.key_value)}
                    className="flex items-center gap-1.5 rounded-lg bg-ink px-3 py-2.5 text-xs font-bold text-white hover:bg-brand"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copier
                  </button>
                </div>
              ) : license.status === "delivered" && license.admin_message ? (
                <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <p className="whitespace-pre-line text-xs leading-relaxed text-emerald-800">
                    <RichText text={license.admin_message} />
                  </p>
                  {license.credentials && (
                    <code className="mt-2 block rounded-md bg-white px-3 py-2 font-mono text-xs font-bold text-emerald-700">
                      {license.credentials}
                    </code>
                  )}
                </div>
              ) : (
                <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  <RichText text={license.instructions} />
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <Empty label="Aucune licence pour l'instant." />
      )}
    </Panel>
  );
}

export function AccountDownloads() {
  const { data, loading } = useFetch("/my/downloads");
  const [expanded, setExpanded] = useState({});
  const [details, setDetails] = useState({});
  const [revealed, setRevealed] = useState({});
  const [shown, setShown] = useState({});

  const fmtSize = (b) =>
    !b ? "" : b >= 1e9 ? `${(b / 1e9).toFixed(2)} Go` : b >= 1e6 ? `${(b / 1e6).toFixed(2)} Mo` : `${Math.max(1, Math.round(b / 1e3))} Ko`;

  const toggle = async (card) => {
    const id = card.order_id;
    if (expanded[id]) {
      setExpanded((e) => ({ ...e, [id]: false }));
      return;
    }
    setExpanded((e) => ({ ...e, [id]: true }));
    if (!details[id]) {
      try {
        const { data: payload } = await api.get(`/my/delivery/${id}`);
        setDetails((d) => ({ ...d, [id]: payload }));
      } catch {
        /* accès verrouillé ou erreur réseau */
      }
    }
  };

  const copyKey = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Clé copiée !");
    } catch {
      toast.error("Copie impossible — sélectionnez la clé manuellement");
    }
  };

  return (
    <Panel title="Mes téléchargements" description="Vos logiciels, jeux et produits numériques achetés">
      {loading ? (
        <Spinner />
      ) : data?.length ? (
        <div className="space-y-3" data-testid="downloads-list">
          {data.map((card) => {
            const unlocked = card.status === "released";
            const det = details[card.order_id];
            return (
              <div key={card.order_id} className="overflow-hidden rounded-2xl border border-line" data-testid={`download-card-${card.order_number}`}>
                <div className="flex flex-wrap items-center gap-3 p-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-brand-light">
                    {card.image_url ? (
                      <img src={card.image_url} alt="" className="h-full w-full object-contain" />
                    ) : (
                      <Package className="h-5 w-5 text-brand" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-ink">{card.product_name || card.order_number}</p>
                    <p className="truncate text-xs text-slate-500">
                      Commande {card.order_number} · {formatDate(card.created_at)}
                    </p>
                  </div>
                  <span
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold ${
                      unlocked ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"
                    }`}
                    data-testid={`download-status-${card.order_number}`}
                  >
                    {unlocked ? <><Check className="h-3 w-3" /> Débloqué</> : <><Lock className="h-3 w-3" /> Verrouillé</>}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggle(card)}
                    data-testid={`download-toggle-${card.order_number}`}
                    className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors duration-200 ${
                      unlocked ? "bg-brand text-white hover:bg-brand/90" : "border border-line text-ink hover:border-brand hover:text-brand"
                    }`}
                  >
                    <Download className="h-3.5 w-3.5" />
                    {expanded[card.order_id] ? "Masquer" : unlocked ? "Voir les fichiers" : "Détails"}
                    {expanded[card.order_id] ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                </div>

                {expanded[card.order_id] && (
                  <div className="border-t border-line bg-surface/60 p-4" data-testid={`download-panel-${card.order_number}`}>
                    {!unlocked ? (
                      <p
                        className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-semibold leading-relaxed text-amber-700"
                        data-testid={`download-locked-${card.order_number}`}
                      >
                        <Lock className="mt-0.5 h-4 w-4 shrink-0" />
                        Paiement en attente de confirmation — vos fichiers et liens seront débloqués automatiquement dès que votre commande sera validée.
                      </p>
                    ) : !det ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin text-brand" />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {(det.version || det.instructions || det.important_info) && (
                          <div className="space-y-2 rounded-xl border border-line bg-white p-3" data-testid={`download-info-${card.order_number}`}>
                            {det.version && (
                              <p className="text-xs text-slate-600">
                                <span className="font-bold text-ink">Version :</span> {det.version}
                              </p>
                            )}
                            {det.instructions && (
                              <div className="text-xs text-slate-600">
                                <p className="mb-0.5 font-bold text-ink">Instructions d'installation</p>
                                <p className="whitespace-pre-line leading-relaxed"><RichText text={det.instructions} /></p>
                              </div>
                            )}
                            {det.important_info && (
                              <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
                                <p className="mb-0.5 font-bold">Informations importantes</p>
                                <p className="whitespace-pre-line leading-relaxed">{det.important_info}</p>
                              </div>
                            )}
                          </div>
                        )}
                        {det.custom_message && (
                          <p className="rounded-xl border border-brand/20 bg-brand-soft/40 p-3 text-xs leading-relaxed text-ink">
                            <RichText text={det.custom_message} />
                          </p>
                        )}
                        <div className="space-y-2">
                          {(det.items || []).map((item) => {
                            const exhausted = item.download_limit > 0 && item.downloads_used >= item.download_limit;
                            const blocked = item.is_expired || exhausted;
                            return (
                              <div
                                key={item.id}
                                className="flex flex-wrap items-center gap-2.5 rounded-lg border border-line bg-white p-2.5"
                                data-testid={`download-content-${card.order_number}-${item.id}`}
                              >
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-light text-brand">
                                  {item.type === "key" ? <KeyRound className="h-3.5 w-3.5" />
                                    : item.type === "link" ? <ExternalLink className="h-3.5 w-3.5" />
                                    : item.type === "text" || item.type === "image" ? <Eye className="h-3.5 w-3.5" />
                                    : <Download className="h-3.5 w-3.5" />}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-xs font-bold text-ink">{item.title}</p>
                                  <p className="truncate text-[11px] text-slate-400">
                                    {item.file_name ? `${item.file_name} · ${fmtSize(item.file_size)} · ` : ""}
                                    {item.expires_at ? `Expire le ${item.expires_at} · ` : ""}
                                    {item.download_limit > 0
                                      ? `Téléchargements : ${item.downloads_used} / ${item.download_limit}`
                                      : "Accès illimité"}
                                  </p>
                                  {item.description && <p className="truncate text-[11px] text-slate-400">{item.description}</p>}
                                  {item.type === "key" && revealed[item.id] && (
                                    <code className="mt-1 block w-fit rounded bg-slate-50 px-2 py-1 font-mono text-[11px] font-bold text-brand">{item.value}</code>
                                  )}
                                  {item.type === "text" && shown[item.id] && (
                                    <p className="mt-1 whitespace-pre-line rounded bg-slate-50 p-2 text-[11px] leading-relaxed text-slate-600">{item.content}</p>
                                  )}
                                </div>
                                {item.is_expired ? (
                                  <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[10px] font-bold text-rose-700" data-testid={`download-expired-${item.id}`}>Expiré</span>
                                ) : exhausted ? (
                                  <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-bold text-amber-700" data-testid={`download-exhausted-${item.id}`}>Limite atteinte</span>
                                ) : null}
                                {item.type === "file" || item.type === "image" ? (
                                  <Link
                                    to={blocked ? "#" : `/compte/commandes/${card.order_id}/telechargement/${item.id}`}
                                    data-testid={`download-file-${item.id}`}
                                    onClick={blocked ? (e) => e.preventDefault() : undefined}
                                    className={`flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold ${
                                      blocked ? "cursor-not-allowed bg-slate-100 text-slate-400" : "bg-brand text-white hover:bg-brand/90"
                                    }`}
                                  >
                                    <Download className="h-3 w-3" /> Télécharger
                                  </Link>
                                ) : item.type === "link" ? (
                                  <button
                                    type="button"
                                    data-testid={`download-link-${item.id}`}
                                    disabled={blocked}
                                    onClick={() => (blocked || !item.url ? toast.error("Ce lien a expiré") : window.open(item.url, "_blank", "noopener"))}
                                    className={`flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-bold ${
                                      blocked ? "cursor-not-allowed border-line text-slate-400" : "border-brand/30 text-brand hover:bg-brand-light"
                                    }`}
                                  >
                                    <ExternalLink className="h-3 w-3" /> Accéder au lien
                                  </button>
                                ) : item.type === "key" ? (
                                  <span className="flex items-center gap-1.5">
                                    <button
                                      type="button"
                                      data-testid={`download-key-${item.id}`}
                                      onClick={() => setRevealed((r) => ({ ...r, [item.id]: !r[item.id] }))}
                                      className="flex items-center gap-1.5 rounded-full border border-brand/30 px-3 py-2 text-xs font-bold text-brand hover:bg-brand-light"
                                    >
                                      {revealed[item.id] ? <EyeOff className="h-3 w-3" /> : <KeyRound className="h-3 w-3" />}
                                      {revealed[item.id] ? "Masquer" : "Afficher la clé"}
                                    </button>
                                    {revealed[item.id] && (
                                      <button
                                        type="button"
                                        data-testid={`download-copy-${item.id}`}
                                        onClick={() => copyKey(item.value)}
                                        className="flex items-center gap-1 rounded-full border border-line px-3 py-2 text-xs font-bold text-slate-600 hover:border-brand hover:text-brand"
                                      >
                                        <Copy className="h-3 w-3" /> Copier
                                      </button>
                                    )}
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    data-testid={`download-text-${item.id}`}
                                    onClick={() => setShown((s) => ({ ...s, [item.id]: !s[item.id] }))}
                                    className="flex items-center gap-1.5 rounded-full border border-brand/30 px-3 py-2 text-xs font-bold text-brand hover:bg-brand-light"
                                  >
                                    <Eye className="h-3 w-3" /> {shown[item.id] ? "Masquer" : "Voir le contenu"}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                          {!(det.items || []).length && (
                            <p className="rounded-xl border border-dashed border-line py-4 text-center text-xs text-slate-400">
                              Aucun contenu numérique pour cette commande.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <Empty label="Aucun téléchargement disponible pour vos produits." />
      )}
    </Panel>
  );
}

export function AccountInvoices() {
  const { data, loading } = useFetch("/my/invoices");
  const { currency } = useStore();

  return (
    <Panel title="Mes factures" description="Téléchargez ou imprimez vos justificatifs">
      {loading ? (
        <Spinner />
      ) : data?.length ? (
        <div className="overflow-x-auto" data-testid="invoices-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-slate-400">
                <th className="pb-3">Facture</th>
                <th className="pb-3">Date</th>
                <th className="pb-3">Paiement</th>
                <th className="pb-3 text-right">Montant</th>
                <th className="pb-3" />
              </tr>
            </thead>
            <tbody>
              {data.map((invoice) => (
                <tr key={invoice.invoice_number} className="border-b border-line last:border-0">
                  <td className="py-3 font-bold text-ink">{invoice.invoice_number}</td>
                  <td className="py-3 text-slate-500">{formatDate(invoice.created_at)}</td>
                  <td className="py-3 text-slate-500">{PAYMENT_LABELS[invoice.payment_method]}</td>
                  <td className="py-3 text-right font-bold text-ink">{formatPrice(invoice.total, currency)}</td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <OrderReceiptButton orderId={invoice.order_id} orderNumber={invoice.order_number} compact />
                      <Link
                        to={`/commande/${invoice.order_number}`}
                        data-testid={`invoice-view-${invoice.invoice_number}`}
                        className="text-xs font-bold text-brand hover:underline"
                      >
                        Voir
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty label="Aucune facture disponible." />
      )}
    </Panel>
  );
}

export function AccountSubscriptions() {
  const { data, loading } = useFetch("/my/subscriptions");
  return (
    <Panel title="Mes abonnements" description="Produits en abonnement liés à votre compte">
      {loading ? (
        <Spinner />
      ) : data?.length ? (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="subscriptions-list">
          {data.map((sub, i) => (
            <div key={i} className="flex items-center gap-3 rounded-xl border border-line p-4">
              <img src={sub.image_url} alt="" className="h-14 w-14 rounded-lg bg-slate-50 object-contain p-1" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-ink">{sub.product_name}</p>
                <p className="text-xs text-slate-500">
                  {sub.duration} · {sub.devices} appareil{sub.devices > 1 ? "s" : ""}
                </p>
                <p className="mt-1 flex items-center gap-1.5 text-[11px] font-bold text-emerald-600">
                  <RefreshCw className="h-3 w-3" /> Actif depuis {formatDate(sub.started_at)}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Empty label="Aucun abonnement actif." />
      )}
    </Panel>
  );
}

export function AccountTickets() {
  const { data, loading, reload } = useFetch("/my/tickets");
  const [form, setForm] = useState({ subject: "", category: "general", message: "" });
  const [replyFor, setReplyFor] = useState(null);
  const [reply, setReply] = useState("");
  const [creating, setCreating] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setCreating(true);
    try {
      await api.post("/my/tickets", form);
      setForm({ subject: "", category: "general", message: "" });
      toast.success("Ticket créé");
      reload();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setCreating(false);
    }
  };

  const sendReply = async (ticketId) => {
    if (!reply.trim()) return;
    try {
      await api.post(`/my/tickets/${ticketId}/reply`, { message: reply });
      setReply("");
      setReplyFor(null);
      reload();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  return (
    <div className="space-y-6">
      <Panel title="Nouveau ticket" description="Notre équipe répond sous 2 heures ouvrées">
        <form onSubmit={submit} className="space-y-3" data-testid="account-ticket-form">
          <input
            data-testid="account-ticket-subject"
            required
            minLength={3}
            placeholder="Sujet"
            value={form.subject}
            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
            className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none"
          />
          <select
            data-testid="account-ticket-category"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none"
          >
            <option value="general">Question générale</option>
            <option value="commande">Ma commande</option>
            <option value="activation">Activation de licence</option>
            <option value="paiement">Paiement</option>
            <option value="deblocage">Déblocage & services</option>
          </select>
          <textarea
            data-testid="account-ticket-message"
            required
            minLength={5}
            rows={4}
            placeholder="Votre message"
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            className="w-full rounded-xl border border-line p-4 text-sm focus:border-brand focus:outline-none"
          />
          <button
            type="submit"
            data-testid="account-ticket-submit"
            disabled={creating}
            className="flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-bold text-white disabled:opacity-60"
          >
            <Plus className="h-4 w-4" /> Créer le ticket
          </button>
        </form>
      </Panel>

      <Panel title="Mes tickets" description="Suivi de vos demandes">
        {loading ? (
          <Spinner />
        ) : data?.length ? (
          <div className="space-y-3" data-testid="tickets-list">
            {data.map((ticket) => (
              <div key={ticket.id} className="rounded-xl border border-line p-4" data-testid={`ticket-${ticket.reference}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-bold text-ink">{ticket.subject}</p>
                  <span
                    className={`rounded-full border px-3 py-1 text-[11px] font-bold ${
                      ticket.status === "closed"
                        ? "border-slate-200 bg-slate-100 text-slate-500"
                        : ticket.status === "answered"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-amber-200 bg-amber-50 text-amber-700"
                    }`}
                  >
                    {ticket.status === "closed" ? "Fermé" : ticket.status === "answered" ? "Répondu" : "Ouvert"}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {ticket.reference} · {formatDateTime(ticket.created_at)}
                </p>

                <div className="mt-3 space-y-2">
                  {ticket.messages.map((message, i) => (
                    <div
                      key={i}
                      className={`rounded-lg p-3 text-xs leading-relaxed ${
                        message.author === "support" ? "bg-brand-soft text-brand" : "bg-slate-50 text-slate-600"
                      }`}
                    >
                      <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide opacity-70">
                        {message.name} · {formatDateTime(message.created_at)}
                      </span>
                      {message.message}
                    </div>
                  ))}
                </div>

                {replyFor === ticket.id ? (
                  <div className="mt-3 flex gap-2">
                    <input
                      data-testid={`ticket-reply-input-${ticket.reference}`}
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Votre réponse"
                      className="h-11 flex-1 rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none"
                    />
                    <button
                      data-testid={`ticket-reply-send-${ticket.reference}`}
                      onClick={() => sendReply(ticket.id)}
                      className="flex items-center gap-1.5 rounded-lg bg-brand px-4 text-xs font-bold text-white"
                    >
                      <Send className="h-3.5 w-3.5" /> Envoyer
                    </button>
                  </div>
                ) : (
                  <button
                    data-testid={`ticket-reply-btn-${ticket.reference}`}
                    onClick={() => setReplyFor(ticket.id)}
                    className="mt-3 text-xs font-bold text-brand hover:underline"
                  >
                    Répondre
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <Empty label="Aucun ticket pour le moment." />
        )}
      </Panel>
    </div>
  );
}

export function AccountProfile() {
  const { user, loadUser } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", phone: user.phone || "" });
  }, [user]);

  const submit = async (event) => {
    event.preventDefault();
    if (form.phone && !isValidPhoneNumber(form.phone)) {
      toast.error("Numéro de téléphone invalide. Vérifiez l'indicatif du pays et le nombre de chiffres.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/profile", form);
      await loadUser();
      toast.success("Informations mises à jour");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="Informations personnelles" description="Ces informations apparaissent sur vos factures">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="profile-form">
        <label className="block">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Nom complet</span>
          <input
            data-testid="profile-name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none"
          />
        </label>
        <PhoneField
          testId="profile"
          label="Téléphone"
          value={form.phone}
          onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
        />
        <label className="block sm:col-span-2">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Email</span>
          <input
            value={user?.email || ""}
            disabled
            className="h-12 w-full rounded-xl border border-line bg-slate-50 px-4 text-sm text-slate-500"
          />
        </label>
        <button
          type="submit"
          data-testid="profile-save"
          disabled={saving}
          className="rounded-full bg-brand px-6 py-3 text-sm font-bold text-white disabled:opacity-60 sm:w-fit"
        >
          Enregistrer
        </button>
      </form>
    </Panel>
  );
}

export function AccountSecurity() {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/change-password", form);
      setForm({ current_password: "", new_password: "" });
      toast.success("Mot de passe mis à jour");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="Sécurité" description="Modifiez votre mot de passe (haché avec bcrypt côté serveur)">
      <form onSubmit={submit} className="max-w-md space-y-4" data-testid="security-form">
        <label className="block">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Mot de passe actuel
          </span>
          <input
            data-testid="security-current"
            type="password"
            value={form.current_password}
            onChange={(e) => setForm((f) => ({ ...f, current_password: e.target.value }))}
            className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Nouveau mot de passe
          </span>
          <input
            data-testid="security-new"
            required
            minLength={6}
            type="password"
            value={form.new_password}
            onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))}
            className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none"
          />
        </label>
        <button
          type="submit"
          data-testid="security-save"
          disabled={saving}
          className="rounded-full bg-brand px-6 py-3 text-sm font-bold text-white disabled:opacity-60"
        >
          Mettre à jour
        </button>
      </form>
    </Panel>
  );
}
